/**
 * audio-engine-registry.js — coordinator for heavy-ML audio engines.
 *
 * Problem this solves:
 *   /edit-video-sound (and future tools) will offer multiple heavy ML
 *   pipelines: DFN3 noise removal (~17 MB), Demucs voice isolation
 *   (~30-80 MB), Whisper-tiny filler-word detection (~39 MB). Loading all
 *   three concurrently can OOM a 4 GB device. We need a coordinator that:
 *
 *     1. Tracks which engines hold ML model RAM right now.
 *     2. On `activate(name)`, if total RAM cost would exceed budget, evicts
 *        LRU engines first by calling their unload() hooks.
 *     3. Emits telemetry so the dashboard can correlate evict decisions
 *        with subsequent OOM telemetry.
 *
 * Public API:
 *   __engineRegistry.register(name, {unload, sizeMB})
 *   __engineRegistry.activate(name): Promise<void>     // marks recently used
 *   __engineRegistry.deactivate(name): Promise<void>   // explicit unload
 *   __engineRegistry.isActive(name): boolean
 *   __engineRegistry.getActive(): string[]
 *   __engineRegistry.getBudgetMB(): number
 *
 * NOT a generic dependency-injection container — only audio-ML engines
 * with non-trivial RAM. Light primitives (RNNoise 110 KB, FFmpeg WASM
 * already mediated by FFmpegProcessor) don't need this — they live for
 * the page lifetime.
 */
(function(){
    'use strict';

    if (window.__engineRegistry) return;

    /**
     * Total RAM budget for active heavy ML engines combined.
     * Uses 30% of device memory as a soft ceiling — leaves headroom for
     * FFmpeg WASM (~32 MB) + browser + decoded AudioBuffer + other tabs.
     */
    function budgetMB() {
        var memGB = (navigator.deviceMemory || 4);
        return Math.floor(memGB * 1024 * 0.30);
    }

    var _engines = Object.create(null); // name → {unload, sizeMB, lastUsed}
    var _active = [];                   // names in MRU order (front = newest)

    function emitTele(action, payload) {
        if (typeof window.__ae !== 'function') return;
        try { window.__ae('engine_registry_' + action, payload || {}); } catch(_){}
    }

    /**
     * Register an engine. Idempotent — calling twice with the same name
     * updates the unload fn + size. NOT activated by registration alone.
     */
    function register(name, opts) {
        opts = opts || {};
        if (typeof name !== 'string' || !name) throw new Error('engine name required');
        if (typeof opts.unload !== 'function') throw new Error('opts.unload required');
        if (typeof opts.sizeMB !== 'number' || opts.sizeMB <= 0) throw new Error('opts.sizeMB required (positive number)');
        _engines[name] = {
            unload: opts.unload,
            sizeMB: opts.sizeMB,
            lastUsed: 0
        };
    }

    /**
     * Mark an engine as active (newly-loaded or just-used). Auto-evicts LRU
     * engines if combined size would exceed budget.
     *
     * `activate` does NOT actually load the engine — that's the caller's
     * responsibility (via heavy-asset-manager). The registry only tracks
     * memory accounting + eviction.
     *
     * Returns the list of engine names that were evicted (empty array if none).
     */
    async function activate(name) {
        if (!_engines[name]) throw new Error('engine "' + name + '" not registered');

        var now = Date.now();
        _engines[name].lastUsed = now;

        // Move to front of MRU
        var idx = _active.indexOf(name);
        if (idx >= 0) _active.splice(idx, 1);
        _active.unshift(name);

        var evicted = await _enforceBudget(name);
        emitTele('activate', { name: name, evicted: evicted, active: _active.slice() });
        return evicted;
    }

    async function _enforceBudget(protectedName) {
        var budget = budgetMB();
        var totalMB = 0;
        _active.forEach(function(n){ totalMB += (_engines[n] && _engines[n].sizeMB) || 0; });

        var evicted = [];
        // Evict from tail (LRU) until we fit; never evict protectedName.
        while (totalMB > budget && _active.length > 1) {
            // Find the oldest engine that isn't protected.
            var victimIdx = -1;
            for (var i = _active.length - 1; i >= 0; i--) {
                if (_active[i] !== protectedName) { victimIdx = i; break; }
            }
            if (victimIdx < 0) break;

            var victim = _active[victimIdx];
            _active.splice(victimIdx, 1);
            evicted.push(victim);
            totalMB -= (_engines[victim] && _engines[victim].sizeMB) || 0;

            try {
                await _engines[victim].unload();
            } catch (e) {
                // Unload failures are non-fatal — log and continue. The model
                // probably still holds memory; budget accounting will be off
                // until the engine is re-activated, but we surfaced the problem.
                emitTele('unload_failed', {
                    name: victim,
                    err: ((e && e.message) || String(e)).substring(0, 200)
                });
            }
        }
        return evicted;
    }

    async function deactivate(name) {
        var idx = _active.indexOf(name);
        if (idx < 0) return; // already inactive
        _active.splice(idx, 1);
        if (_engines[name]) {
            try {
                await _engines[name].unload();
                emitTele('deactivate', { name: name });
            } catch (e) {
                emitTele('unload_failed', {
                    name: name,
                    err: ((e && e.message) || String(e)).substring(0, 200)
                });
                throw e;
            }
        }
    }

    function isActive(name) {
        return _active.indexOf(name) >= 0;
    }

    function getActive() {
        return _active.slice();
    }

    window.__engineRegistry = Object.freeze({
        register: register,
        activate: activate,
        deactivate: deactivate,
        isActive: isActive,
        getActive: getActive,
        getBudgetMB: budgetMB
    });
})();
